import { forwardRef } from "react"
import moment from "moment"

// 230726 (permintaan user): FAKTUR PELUNASAN debit note (dipakai react-to-print
// dari list.jsx) — dicetak otomatis setelah pelunasan (termasuk pelunasan memakai
// credit note customer) dan bisa dicetak ulang dari tombol Faktur di baris debit
// note. Menampilkan seluruh riwayat pelunasan (usages TYPE_SETTLEMENT).

const formatRupiah = (value) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 })
    .format(Number(value || 0))

const methodLabel = (usage) => {
  const method = String(usage?.payment_method_type || "").toUpperCase()
  if (method === "CASH") return "Cash"
  if (method === "TRANSFER") return "Transfer"
  if (method === "CREDIT_NOTE") {
    // Nomor credit note dititip di description: "... | credit_note=CN-xxx, CN-yyy"
    const m = /credit_note=([^|]+)/.exec(String(usage?.description || ""))
    return m ? `Credit Note (${m[1].trim()})` : "Credit Note"
  }
  return method || "-"
}

const SettlementFakturDocument = forwardRef(({ note, settlements = [], outletName, printedBy }, ref) => {
  if (!note) return null

  const totalSettled = settlements.reduce((acc, row) => acc + Number(row?.used_amount || 0), 0)

  return (
    <div ref={ref} className="hidden print:block p-3 text-sm text-black">
      <style>{`@page { size: A4; margin: 8mm 10mm; }`}</style>

      <div className="flex items-start justify-between border-b-2 border-black pb-2">
        <div>
          <div className="text-lg font-bold">FAKTUR PELUNASAN</div>
          <div className="text-xs">Debit Note No: {note.note_number || "-"}</div>
        </div>
        <div className="text-right text-xs">
          <div className="font-semibold">{outletName || `Outlet ${note.outlet_id ?? "-"}`}</div>
          <div>Tanggal Cetak: {moment().format("DD MMMM YYYY HH:mm")}</div>
        </div>
      </div>

      <table className="w-full mt-3 text-sm">
        <tbody>
          <tr>
            <td className="py-0.5 w-40 align-top">Customer</td>
            <td className="py-0.5 w-3 align-top">:</td>
            <td className="py-0.5 align-top font-medium">
              {note.customer_name || note.customer_id || "-"}
              {note.customer_phone ? ` (${note.customer_phone})` : ""}
            </td>
          </tr>
          <tr>
            <td className="py-0.5 align-top">Nominal Debit Note</td>
            <td className="py-0.5 align-top">:</td>
            <td className="py-0.5 align-top font-bold">{formatRupiah(note.original_amount)}</td>
          </tr>
          <tr>
            <td className="py-0.5 align-top">Tanggal Terbit</td>
            <td className="py-0.5 align-top">:</td>
            <td className="py-0.5 align-top">
              {note.issue_date ? moment(note.issue_date).format("DD MMMM YYYY") : "-"}
            </td>
          </tr>
        </tbody>
      </table>

      <table className="w-full mt-4 text-sm border-collapse">
        <thead>
          <tr>
            <th className="py-1 text-left border-y border-black w-8">No</th>
            <th className="py-1 text-left border-y border-black">Tanggal Bayar</th>
            <th className="py-1 text-left border-y border-black">Metode</th>
            <th className="py-1 text-right border-y border-black">Nominal</th>
          </tr>
        </thead>
        <tbody>
          {settlements.length === 0 && (
            <tr>
              <td colSpan={4} className="py-2 text-center text-neutral-600">Belum ada pelunasan.</td>
            </tr>
          )}
          {settlements.map((row, idx) => (
            <tr key={row.id || idx}>
              <td className="py-1 align-top">{idx + 1}</td>
              <td className="py-1 align-top">
                {row.created_at ? moment(row.created_at).format("DD/MM/YYYY HH:mm") : "-"}
              </td>
              <td className="py-1 align-top">{methodLabel(row)}</td>
              <td className="py-1 align-top text-right">{formatRupiah(row.used_amount)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-bold">
            <td colSpan={3} className="py-1 text-right border-t border-black">Total Dibayar</td>
            <td className="py-1 text-right border-t border-black">{formatRupiah(totalSettled)}</td>
          </tr>
          <tr className="font-bold">
            <td colSpan={3} className="py-1 text-right">Sisa Tagihan</td>
            <td className="py-1 text-right">{formatRupiah(note.remaining_amount)}</td>
          </tr>
        </tfoot>
      </table>

      {String(note.status || "").toUpperCase() === "USED" || Number(note.remaining_amount || 0) <= 0 ? (
        <div className="mt-2 text-sm font-bold">Status: LUNAS</div>
      ) : (
        <div className="mt-2 text-sm">Status: Belum lunas (sisa {formatRupiah(note.remaining_amount)})</div>
      )}

      <div className="flex justify-center gap-10 mt-6 text-center text-xs">
        <div className="w-56">
          <div>Diketahui,</div>
          <div className="mt-8 border-t border-black pt-1">&nbsp;</div>
        </div>
        <div className="w-56">
          <div>Dibuat Oleh,</div>
          <div className="mt-8 border-t border-black pt-1">{printedBy || " "}</div>
        </div>
      </div>
    </div>
  )
})

export default SettlementFakturDocument
